import {
  Box,
  Button,
  Card,
  Center,
  Flex,
  Icon,
  IconButton,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Text,
  useClipboard,
} from "@chakra-ui/react";
import { addSafeSendSecret, useAppDispatch, useAppSelector } from "@umami/state";
import { useCallback, useEffect } from "react";

import {
  CopyIcon,
  FacebookIcon,
  MailIcon,
  SafeSendIcon,
  TelegramIcon,
  WhatsAppIcon,
} from "../../assets/icons";
import { useColor } from "../../styles/useColor";

export const sharePlatforms = {
  WHATSAPP: (text: string) => `https://wa.me/?text=${text}`,
  TELEGRAM: (text: string, subject: string) => `https://t.me/share/url?url=${subject}&text=${text}`,
  FACEBOOK: (text: string) => `https://www.facebook.com/sharer/sharer.php?u=${text}`,
  MAIL: (text: string, subject: string) => `mailto:?subject=${subject}&body=${text}`,
} as const;

type ShareProps = {
  safeSendHash: string;
  secret: string;
};

const isMobileDevice = () =>
  /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

const handleSocialShare = (platform: string, secret: string) => {
  const text = encodeURIComponent(`Safe Send Key: ${secret}`);
  const subject = encodeURIComponent("Safe Send Key");

  let url = "";

  switch (platform) {
    case "whatsapp":
      url = sharePlatforms.WHATSAPP(text);
      break;
    case "telegram":
      url = sharePlatforms.TELEGRAM(text, subject);
      break;
    case "facebook":
      url = sharePlatforms.FACEBOOK(text);
      break;
    case "mail":
      url = sharePlatforms.MAIL(text, subject);
      break;
  }

  if (url) {
    window.open(url, "_blank", "noopener,noreferrer");
  }
};

export const ShareSecretKeyBox = ({ safeSendHash, secret }: ShareProps) => {
  const color = useColor();
  const { hasCopied, onCopy } = useClipboard(secret);
  const secrets = useAppSelector(state => state.safeSendSecrets.secrets);
  const hash = safeSendHash;
  const dispatch = useAppDispatch();

  const addSafeSendSecretToStore = useCallback(() => {
    if (secret && !secrets[hash]) {
      dispatch(addSafeSendSecret({ hash, secret }));
    }
  }, [dispatch, secret, hash, secrets]);

  useEffect(() => {
    addSafeSendSecretToStore();
  }, [addSafeSendSecretToStore]);

  const hasNativeShare = "share" in navigator && isMobileDevice();

  const handleNativeShare = async () => {
    if (hasNativeShare) {
      try {
        await navigator.share({
          title: "Safe Send Key",
          text: `Here's your Safe Send key: ${secret}`,
        });
      } catch (error) {
        console.error("Error sharing:", error);
      }
    }
  };

  const SocialShareButton = ({
    icon,
    platform,
    label,
  }: {
    icon: React.ElementType;
    platform: string;
    label: string;
  }) => (
    <IconButton
      color={color("600")}
      background={color("white", "black")}
      border="none"
      borderRadius="full"
      _hover={{ background: color("200", "700") }}
      aria-label={label}
      icon={<Icon as={icon} boxSize="32px" />}
      onClick={() => handleSocialShare(platform, secret)}
      size="lg"
    />
  );

  return (
    <Card
      flexDirection="column"
      gap="16px"
      display="flex"
      width="100%"
      padding={{ base: "20px 12px", md: "24px 16px" }}
      background={color("100")}
      borderRadius="24px"
    >
      <Center width="100%">
        <SafeSendIcon width="26px" height="24px" />
      </Center>

      <Box textAlign="center">
        <Text
          marginBottom="6px"
          color={color("700")}
          fontSize="16px"
          fontWeight="600"
          lineHeight="18px"
        >
          Safe Send Key
        </Text>
        <Text color={color("500")} fontSize="12px" fontWeight="400" lineHeight="18px">
          Share the key with receiver for them to claim the transaction.
        </Text>
      </Box>

      <Flex
        alignItems="center"
        height="48px"
        background={color("white")}
        borderRadius="6px"
        paddingX="16px"
      >
        <Text
          flex="1"
          overflow="hidden"
          color={color("900")}
          fontSize={{ base: "16px", md: "18px" }}
          fontWeight="400"
          lineHeight="24px"
          whiteSpace="nowrap"
          textOverflow="ellipsis"
        >
          {secret}
        </Text>
        <Popover autoFocus={false} isOpen={hasCopied} onOpen={onCopy}>
          <PopoverTrigger>
            <IconButton
              width="24px"
              aria-label={hasCopied ? "Copied!" : "Copy Secret"}
              icon={<CopyIcon />}
              size="sm"
              variant="ghost"
            />
          </PopoverTrigger>
          <PopoverContent maxWidth="max-content" background="white">
            <PopoverArrow background="white !important" />
            <PopoverBody>
              <Text color={color("black")} fontSize="sm">
                Copied!
              </Text>
            </PopoverBody>
          </PopoverContent>
        </Popover>
      </Flex>

      {hasNativeShare ? (
        <Button
          width="100%"
          data-testid="share-key-btn"
          onClick={handleNativeShare}
          variant="primary"
        >
          Share key
        </Button>
      ) : (
        <>
          <Text
            color={color("600")}
            fontSize="14px"
            fontWeight="500"
            lineHeight="20px"
            textAlign="center"
          >
            Or share this link via
          </Text>

          <Flex
            justifyContent="center"
            flexWrap="wrap"
            gap="16px"
            data-testid="social-share-buttons"
          >
            <SocialShareButton icon={FacebookIcon} label="Share on Facebook" platform="facebook" />
            <SocialShareButton icon={WhatsAppIcon} label="Share on WhatsApp" platform="whatsapp" />
            <SocialShareButton icon={TelegramIcon} label="Share on Telegram" platform="telegram" />
            <SocialShareButton icon={MailIcon} label="Share via Email" platform="mail" />
          </Flex>
        </>
      )}
    </Card>
  );
};
